import React, { useEffect, useState } from 'react';
import { IProduct, IProductUnit } from '../types/strapi';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Box from '@material-ui/core/Box';

import { IOrderProduct } from '../types/orderProduct';
import { calcProductTotal } from '../helpers/utils';
import { ShopMode } from '../redux/types';
import { PriceDisplay } from './PriceDisplay';

const useStyles = makeStyles((theme) => ({
  title: {
    margin: 0,
    padding: theme.spacing(2),
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
  },
  closeButton: {
    position: 'relative',
    color: theme.palette.grey[500],
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  description: {
    color: theme.palette.primary.main,
  },
  detail: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  detailLabel: {
    color: theme.palette.primary.main,
  },
  radioGroup: {
    flexDirection: 'row',
  },
}));

interface Props {
  product: IProduct;
  isUserLogged: boolean;
  shopMode?: ShopMode | null;
  onAdd: (orderProduct: IOrderProduct) => void;
  onCancel: () => void;
}

const AddProductModal: React.FC<Props> = ({ product, isUserLogged, shopMode, onAdd, onCancel }) => {
  const classes = useStyles();

  const [productOrder, setProductOrder] = useState<IOrderProduct>({
    ...product,
    quantity: 1,
    quantityUnit: product?.unit || IProductUnit.kg,
  });
  const [addCartModalOpen, setAddCartModalOpen] = useState(false);

  useEffect(() => {
    setAddCartModalOpen(!!product);
    setProductOrder({
      ...product,
      quantity: 1,
      quantityUnit: product?.unit || IProductUnit.kg,
    });
  }, [product]);

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const quantity = parseFloat(event.target.value);
    setProductOrder({ ...productOrder, quantity: Number.isNaN(quantity) ? 0 : quantity });
  };

  const handleClose = () => {
    setAddCartModalOpen(false);
    onCancel();
  };

  const handleConfirm = () => {
    setAddCartModalOpen(false);
    onAdd(productOrder);
  };

  return (
    <Dialog open={addCartModalOpen} onClose={handleClose}>
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h6">Seleziona la quantità</Typography>
        <IconButton aria-label="close" className={classes.closeButton} onClick={() => handleClose()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Typography variant="subtitle1" gutterBottom className={classes.description}>
          {productOrder.name}
        </Typography>
        {isUserLogged && (
          <Box className={classes.detail} style={{ alignItems: 'center' }}>
            <div className={classes.detailLabel}>Prezzo</div>
            <Box display="flex" alignItems="center">
              <PriceDisplay
                price={product.price}
                discountedPrice={product.discountedPrice}
                shopMode={shopMode || ShopMode.LocalDelivery}
                variant="small"
                showBadge
              />
              <Typography variant="body2" style={{ marginLeft: 4 }}>
                / {productOrder.unit}
              </Typography>
            </Box>
          </Box>
        )}
        {productOrder.unit === IProductUnit.kg && (
          <>
            <Typography variant="subtitle2" gutterBottom className={classes.detail}>
              <div className={classes.detailLabel}>Peso medio unitario</div>
              <div>{productOrder.unitWeight} Kg</div>
            </Typography>
            <FormControl component="div" color="primary">
              <RadioGroup
                name="quantityUnit"
                className={classes.radioGroup}
                value={productOrder.quantityUnit}
                onChange={(event) =>
                  setProductOrder({ ...productOrder, quantityUnit: event.target.value as IProductUnit })
                }
              >
                <FormControlLabel value={IProductUnit.kg} control={<Radio color="primary" />} label="Al kg" />
                <FormControlLabel value={IProductUnit.pz} control={<Radio color="primary" />} label="Al pezzo" />
              </RadioGroup>
            </FormControl>
          </>
        )}
        <TextField
          label={`Quantità in ${productOrder.quantityUnit === IProductUnit.kg ? 'kg' : 'pezzi'}`}
          type="number"
          InputLabelProps={{
            shrink: true,
          }}
          variant="outlined"
          defaultValue={productOrder.quantity}
          focused
          color="primary"
          margin="normal"
          InputProps={{
            inputProps: productOrder.quantityUnit === IProductUnit.kg ? { min: 0.1 } : { min: 1, step: 1 },
          }}
          onChange={handleQuantityChange}
        />
        {isUserLogged && productOrder && (
          <Typography variant="subtitle2" gutterBottom className={classes.detail}>
            <div className={classes.detailLabel}>Subtotale</div>
            <div>{`€ ${calcProductTotal(productOrder, shopMode || undefined).toFixed(2)}`}</div>
          </Typography>
        )}
        {productOrder.unit === IProductUnit.kg && productOrder.quantityUnit === IProductUnit.pz && (
          <Typography variant="caption">
            Il subtotale è indicativo in quanto l'effettivo
            <br />
            peso dei prodotti potrebbe essere differente
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleClose}>
          Annulla
        </Button>
        <Button autoFocus onClick={handleConfirm} color="primary" disabled={!productOrder.quantity}>
          Aggiungi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProductModal;
